import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Maximize2, Trash2, X } from "lucide-react";
import clsx from "clsx";
import { Config, Deals } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { Deal, Stage } from "@/api/types";
import { isLost, stageColor, stageRole } from "@/lib/colors";
import { eur, fmtDayShort, pct } from "@/lib/format";
import { confirmDialog, toast } from "@/lib/feedback";
import { useUIStore } from "@/store/ui";
import { Monogram, OverflowMenu, Segmented } from "@/components/ui";
import DealInfoPanel from "@/components/DealInfoPanel";
import OwnersSection from "@/components/OwnersSection";
import { DealContracts } from "@/components/contracts/LinkedContracts";
import HandoffDialog from "./HandoffDialog";
import PeoplePanel from "./PeoplePanel";
import Story from "./Story";

interface Props {
  dealId: string;
  /** Set when shown in the side panel: renders close + "open full page". */
  onClose?: () => void;
}

/** One deal as a conversation: header, stage stepper, the story, and a rail
 *  with the people involved and the deal's details. Used by the side panel
 *  and the /deals/:id page. */
export default function DealThread({ dealId, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const closeDeal = useUIStore((s) => s.closeDeal);
  const [tab, setTab] = useState<"people" | "details">("people");
  // Below `lg` the side rail becomes a tab next to the story.
  const [pane, setPane] = useState<"story" | "side">("story");
  const [handingOff, setHandingOff] = useState(false);

  const dealQuery = useQuery({ queryKey: ["deal", dealId], queryFn: () => Deals.get(dealId) });
  const stagesQuery = useQuery({ queryKey: ["stages"], queryFn: () => Config.listStages() });
  const blockersQuery = useQuery({ queryKey: ["blockers"], queryFn: () => Config.listBlockers() });
  const deal = dealQuery.data;
  const stages = (stagesQuery.data ?? [])
    .filter((s) => s.is_active)
    .sort((a, b) => a.position - b.position);

  const stageMutation = useMutation({
    mutationFn: (stage: string) => Deals.update(dealId, { stage }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["deal", dealId] });
      void qc.invalidateQueries({ queryKey: ["deals"] });
      void qc.invalidateQueries({ queryKey: ["activities", dealId] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.ok(`Moved to ${d.stage}.`);
    },
    onError: (e) => toast.err(`Couldn't change the stage: ${apiErrorMessage(e)}`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => Deals.remove(dealId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deals"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      void qc.invalidateQueries({ queryKey: ["my-work"] });
      toast.ok("Deal deleted.");
      if (onClose) closeDeal();
      else navigate("/deals");
    },
    onError: (e) => toast.err(`Couldn't delete the deal: ${apiErrorMessage(e)}`),
  });

  if (dealQuery.isLoading) {
    return <div className="p-8 text-mut-500">Loading…</div>;
  }
  if (!deal) {
    return (
      <div className="p-8">
        <p className="text-md font-medium">
          This deal doesn't exist, or you don't have access to it.
        </p>
        {onClose && (
          <button className="btn mt-4" onClick={onClose}>
            Close
          </button>
        )}
      </div>
    );
  }

  const role = stageRole(
    deal.stage,
    stages.findIndex((s) => s.name === deal.stage),
  );
  const currentIdx = stages.findIndex((s) => s.name === deal.stage);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <header className="px-4 md:px-6 pt-4 md:pt-5 pb-4 border-b border-fg/[0.07] space-y-3">
        <div className="flex items-start gap-3">
          <Monogram name={deal.lead} role={isLost(deal.stage) ? "amber" : role} size={40} />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg md:text-xl font-semibold tracking-tight truncate">
              {deal.lead}
            </h2>
            <div className="text-sm text-mut-500 flex flex-wrap gap-x-2">
              {deal.account_id && deal.account_name ? (
                <Link
                  to={`/accounts/${deal.account_id}`}
                  onClick={() => onClose && closeDeal()}
                  className="hover:text-accent-azure"
                >
                  {deal.account_name}
                </Link>
              ) : (
                <span>No account</span>
              )}
              <span>·</span>
              <span className="text-mut-100 font-medium tabular-nums">
                {eur(deal.arr, { short: true })} ARR
              </span>
              <span>·</span>
              <span className="tabular-nums">{pct(deal.probability)}</span>
              {deal.next_action && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span
                    className={clsx(
                      "max-sm:basis-full max-sm:truncate",
                      deal.is_past_due && "text-accent-pink font-medium",
                    )}
                  >
                    Next: {deal.next_action}
                    {deal.due_date && `, ${fmtDayShort(deal.due_date)}`}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {deal.can_edit && (
              <button
                className="btn !px-2.5 sm:!px-3"
                aria-label="Hand off"
                onClick={() => setHandingOff(true)}
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Hand off</span>
              </button>
            )}
            {deal.can_edit && (
              <OverflowMenu
                label="Deal actions"
                items={[
                  {
                    label: "Delete deal",
                    icon: <Trash2 className="w-4 h-4" />,
                    danger: true,
                    onClick: async () => {
                      if (
                        await confirmDialog({
                          title: `Delete ${deal.lead}?`,
                          body: "This removes the deal and its whole story. It can't be undone.",
                          confirmLabel: "Delete deal",
                          danger: true,
                        })
                      )
                        deleteMutation.mutate();
                    },
                  },
                ]}
              />
            )}
            {onClose && (
              <>
                <button
                  className="hidden md:inline-flex btn btn-ghost !p-2"
                  title="Open as a page"
                  aria-label="Open as a page"
                  onClick={() => {
                    closeDeal();
                    navigate(`/deals/${deal.id}`);
                  }}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  className="btn btn-ghost !p-2"
                  onClick={onClose}
                  title="Close (Esc)"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <StageStepper
          stages={stages}
          current={deal.stage}
          currentIdx={currentIdx}
          disabled={!deal.can_edit || stageMutation.isPending}
          onPick={(s) => s !== deal.stage && stageMutation.mutate(s)}
        />
      </header>

      <div className="lg:hidden border-b border-fg/[0.07] px-4 py-2">
        <Segmented
          label="Deal section"
          value={pane === "story" ? "story" : tab}
          onChange={(v) => {
            if (v === "story") setPane("story");
            else {
              setTab(v);
              setPane("side");
            }
          }}
          options={[
            { value: "story", label: "Story" },
            { value: "people", label: "People" },
            { value: "details", label: "Details" },
          ]}
        />
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className={clsx("min-h-0 min-w-0", pane !== "story" && "hidden lg:block")}>
          <Story deal={deal} />
        </div>
        <aside
          className={clsx(
            "min-h-0 overflow-y-auto lg:border-l border-fg/[0.07] bg-ink-800/50 px-4 lg:px-5 py-4 space-y-4",
            pane === "story" && "hidden lg:block",
          )}
        >
          <div className="hidden lg:block">
            <Segmented
              label="Deal side panel"
              value={tab}
              onChange={setTab}
              options={[
                { value: "people", label: "People" },
                { value: "details", label: "Details" },
              ]}
            />
          </div>
          {tab === "people" ? (
            <PeoplePanel deal={deal} />
          ) : (
            <DetailsTab
              deal={deal}
              stages={stagesQuery.data ?? []}
              blockers={blockersQuery.data ?? []}
            />
          )}
        </aside>
      </div>

      {handingOff && (
        <HandoffDialog dealId={deal.id} dealLead={deal.lead} onClose={() => setHandingOff(false)} />
      )}
    </div>
  );
}

function DetailsTab({
  deal,
  stages,
  blockers,
}: {
  deal: Deal;
  stages: Stage[];
  blockers: Parameters<typeof DealInfoPanel>[0]["blockers"];
}) {
  // Following a contract link from the side panel should leave the panel.
  const closeDeal = useUIStore((s) => s.closeDeal);
  return (
    <div className="space-y-6">
      <DealContracts dealId={deal.id} accountId={deal.account_id} onNavigate={closeDeal} />
      <DealInfoPanel dealId={deal.id} stages={stages} blockers={blockers} />
      <div className="border-t border-fg/[0.07] pt-4">
        <OwnersSection dealId={deal.id} />
      </div>
    </div>
  );
}

function StageStepper({
  stages,
  current,
  currentIdx,
  disabled,
  onPick,
}: {
  stages: Stage[];
  current: string;
  currentIdx: number;
  disabled: boolean;
  onPick: (stage: string) => void;
}) {
  const currentRef = useRef<HTMLButtonElement>(null);
  // On phones the stages scroll sideways; keep the current one in view.
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [current]);
  return (
    <div
      className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0"
      role="radiogroup"
      aria-label="Stage"
    >
      {stages.map((s, i) => {
        const isCurrent = s.name === current;
        const isPast = currentIdx >= 0 && i < currentIdx && !isLost(s.name);
        const color = stageColor(s.name, s.color);
        return (
          <button
            key={s.id}
            ref={isCurrent ? currentRef : undefined}
            role="radio"
            aria-checked={isCurrent}
            disabled={disabled}
            onClick={() => onPick(s.name)}
            title={disabled ? s.name : `Move to ${s.name}`}
            className={clsx(
              "shrink-0 whitespace-nowrap px-2.5 py-1 rounded-full text-sm font-medium transition-[filter,background] disabled:cursor-default",
              !disabled && !isCurrent && "hover:brightness-95",
              !isCurrent && !isPast && "bg-fg/[0.05] text-mut-500",
            )}
            style={
              isCurrent
                ? { background: color, color: "#fff" }
                : isPast
                  ? { background: stageColor(s.name, s.color, 0.12), color }
                  : undefined
            }
          >
            {s.name}
          </button>
        );
      })}
    </div>
  );
}
